import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getArticle, getArticleComments, createComment, exportContentPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getCloudinaryUrl, getFullSizeUrl, getMediumUrl, getCoverUrl } from "../lib/cloudinaryUtils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Calendar, MapPin, User, ArrowLeft, X, ChevronLeft, ChevronRight, FileDown } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import AuthorSignature from "../components/AuthorSignature";

// Get photo URL with optional watermark (uses centralized cloudinaryUtils)
const getPhotoUrlWithWatermark = (photo, withWatermark = true) => {
  return getFullSizeUrl(photo, withWatermark);
};

// Helper function to remove orientation transforms from Cloudinary URL
const removeOrientationTransforms = (url) => {
  if (!url || !url.includes('/upload/')) return url;
  
  // Split URL at /upload/
  const parts = url.split('/upload/');
  if (parts.length !== 2) return url;
  
  const base = parts[0] + '/upload/';
  const rest = parts[1];
  
  // Split the rest by '/' to get segments
  const segments = rest.split('/');
  const cleanSegments = [];
  
  for (const seg of segments) {
    // Check if segment contains transforms (has format like "a_90" or "c_fill,a_90")
    if (seg.includes('_') && !seg.startsWith('v') && !seg.includes('.')) {
      // This is a transform segment - remove orientation transforms
      const transforms = seg.split(',');
      const cleanTransforms = transforms.filter(t => 
        !['a_90', 'a_180', 'a_270', 'e_hflip', 'e_vflip'].includes(t)
      );
      if (cleanTransforms.length > 0) {
        cleanSegments.push(cleanTransforms.join(','));
      }
      // If all transforms were orientation, skip this segment entirely
    } else {
      cleanSegments.push(seg);
    }
  }
  
  return base + cleanSegments.join('/');
};

// Get photo URL by ID from photos array
// If block has its own transformations, they OVERRIDE the photo's default transformations
const getPhotoById = (photoId, photos, blockRotation = 0, blockFlipH = false, blockFlipV = false) => {
  const photo = photos.find(p => p.id === photoId);
  if (!photo) return null;
  
  // Get base cloudinary URL
  let url = photo.cloudinary_url;
  if (!url && photo.image_data) {
    return `data:image/jpeg;base64,${photo.image_data}`;
  }
  if (!url) return null;
  
  // If block has its own transformations, use them instead of photo's default
  const hasBlockTransforms = blockRotation || blockFlipH || blockFlipV;
  
  if (hasBlockTransforms && url.includes('/upload/')) {
    // Remove existing orientation transforms from URL
    url = removeOrientationTransforms(url);
    
    // Apply block-level transformations
    const transforms = [];
    if (blockFlipH) transforms.push('e_hflip');
    if (blockFlipV) transforms.push('e_vflip');
    if (blockRotation === 90) transforms.push('a_90');
    else if (blockRotation === 180) transforms.push('a_180');
    else if (blockRotation === 270) transforms.push('a_270');
    
    if (transforms.length > 0) {
      const transformStr = transforms.join(',');
      url = url.replace('/upload/', `/upload/${transformStr}/`);
    }
  } else {
    // No block transforms - use the photo's default URL with its own transforms
    url = getPhotoUrlWithWatermark(photo);
  }
  
  return url;
};

// Get medium-sized photo for article content
const getMediumPhoto = (photo) => {
  return getMediumUrl(photo, 800, 600);
};

// NEW: Component to render article with structured content blocks (magazine style)
function ArticleContentBlocks({ contentBlocks, photos, onPhotoClick }) {
  if (!contentBlocks || contentBlocks.length === 0) return null;
  
  // Build a map of photo_id to index for click handler
  const photoIdToIndex = {};
  photos.forEach((p, idx) => { photoIdToIndex[p.id] = idx; });
  
  return (
    <div className="space-y-8" data-testid="article-content-blocks">
      {contentBlocks.map((block, idx) => {
        switch (block.type) {
          case 'section_title':
            return (
              <h2 
                key={idx} 
                className="font-heading text-2xl md:text-3xl font-bold text-primary mt-12 mb-6 first:mt-0"
                data-testid={`section-title-${idx}`}
              >
                {block.text}
              </h2>
            );
          
          case 'paragraph':
            return (
              <p 
                key={idx} 
                className="text-foreground leading-relaxed text-lg"
                data-testid={`paragraph-${idx}`}
              >
                {block.text}
              </p>
            );
          
          case 'image':
            const photoUrl = block.photo_url || getPhotoById(
              block.photo_id, 
              photos, 
              block.rotation || 0,
              block.flip_horizontal || false,
              block.flip_vertical || false
            );
            if (!photoUrl) return null;
            const photoIdx = photoIdToIndex[block.photo_id] ?? -1;
            return (
              <figure 
                key={idx} 
                className="my-8"
                data-testid={`image-block-${idx}`}
              >
                <button
                  onClick={() => photoIdx >= 0 && onPhotoClick(photoIdx)}
                  className="w-full block overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all cursor-zoom-in"
                >
                  <img
                    src={photoUrl}
                    alt={block.alt || block.caption || `Photo ${idx + 1}`}
                    className="w-full h-auto max-h-[70vh] object-contain bg-gray-100"
                  />
                </button>
                {block.caption && (
                  <figcaption className="mt-3 text-center text-sm text-muted-foreground italic px-4">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          
          case 'image_gallery':
            const photoIds = block.photo_ids || [];
            const galleryRotations = block.rotations || {};
            const galleryFlipsH = block.flips_horizontal || {};
            const galleryFlipsV = block.flips_vertical || {};
            // Generate URLs with per-photo transformations
            const photoUrls = block.photo_urls || photoIds.map(id => {
              const photoRotation = galleryRotations[id] || 0;
              const photoFlipH = galleryFlipsH[id] || false;
              const photoFlipV = galleryFlipsV[id] || false;
              return getPhotoById(id, photos, photoRotation, photoFlipH, photoFlipV);
            }).filter(Boolean);
            if (photoUrls.length === 0) return null;
            return (
              <figure 
                key={idx} 
                className="my-8"
                data-testid={`gallery-block-${idx}`}
              >
                <div className={`grid gap-3 ${photoUrls.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
                  {photoIds.map((pid, pIdx) => {
                    const photoRotation = galleryRotations[pid] || 0;
                    const photoFlipH = galleryFlipsH[pid] || false;
                    const photoFlipV = galleryFlipsV[pid] || false;
                    const url = block.photo_urls ? block.photo_urls[pIdx] : getPhotoById(pid, photos, photoRotation, photoFlipH, photoFlipV);
                    if (!url) return null;
                    const pidx = photoIdToIndex[pid] ?? -1;
                    return (
                      <button
                        key={pIdx}
                        onClick={() => pidx >= 0 && onPhotoClick(pidx)}
                        className="overflow-hidden rounded-lg shadow hover:shadow-lg transition-all cursor-zoom-in aspect-[4/3]"
                      >
                        <img
                          src={url}
                          alt={`Photo ${pIdx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
                {block.caption && (
                  <figcaption className="mt-3 text-center text-sm text-muted-foreground italic px-4">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          
          case 'quote':
            return (
              <blockquote 
                key={idx} 
                className="my-8 pl-6 border-l-4 border-accent italic text-lg text-muted-foreground"
                data-testid={`quote-${idx}`}
              >
                {block.text}
                {block.caption && <footer className="mt-2 not-italic text-sm">— {block.caption}</footer>}
              </blockquote>
            );

          case 'list':
            return (
              <div key={idx} data-testid={`list-block-${idx}`}>
                {block.title && <p className="font-heading text-lg font-semibold text-primary mb-3">{block.title}</p>}
                <ul className="space-y-2 text-lg text-foreground">
                  {(block.items || []).map((it, i) => (
                    <li key={i} className="flex gap-3"><span className="mt-3 h-1.5 w-1.5 rounded-full bg-accent shrink-0" /><span>{String(it)}</span></li>
                  ))}
                </ul>
              </div>
            );

          case 'key_figures':
            return (
              <div key={idx} className="grid grid-cols-2 md:grid-cols-4 gap-4 my-8" data-testid={`key-figures-block-${idx}`}>
                {(block.items || []).map((it, i) => (
                  <div key={i} className="border-t-2 border-accent pt-3">
                    <div className="font-heading text-3xl md:text-4xl font-bold text-primary leading-none">{it.value}</div>
                    <div className="text-sm text-muted-foreground mt-2 leading-snug">{it.label}</div>
                  </div>
                ))}
              </div>
            );

          case 'callout':
            return (
              <aside key={idx} className="my-8 bg-secondary/60 border-l-4 border-accent px-6 py-5 rounded-r" data-testid={`callout-block-${idx}`}>
                {block.title && <div className="text-xs font-semibold uppercase tracking-widest text-accent mb-2">{block.title}</div>}
                <div className="text-foreground leading-relaxed">{block.text}</div>
              </aside>
            );

          case 'links':
            return (
              <nav key={idx} className="mt-12 pt-6 border-t border-border" data-testid={`links-block-${idx}`}>
                {block.title && <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">{block.title}</p>}
                <ul className="space-y-2 text-sm">
                  {(block.links || []).filter((l) => /^\/(?!\/)/.test(l.url || "")).map((l, i) => (
                    <li key={i}>
                      <a href={l.url} className="text-primary underline underline-offset-4 hover:text-accent break-words"
                        {...(/^https?:/.test(l.url || "") ? { target: "_blank", rel: "noopener noreferrer nofollow" } : {})}>{l.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}

// Component to render article content with inline photos (legacy support)
function ArticleContentWithPhotos({ content, photos, onPhotoClick }) {
  // Parse content and find [PHOTO_X] markers
  const parseContent = () => {
    if (!content) return [];
    
    // Split content by photo markers
    const photoPattern = /\[PHOTO_(\d+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Track which photos are used inline
    const usedPhotoIndices = new Set();
    
    while ((match = photoPattern.exec(content)) !== null) {
      // Add text before the marker
      if (match.index > lastIndex) {
        const textBefore = content.substring(lastIndex, match.index).trim();
        if (textBefore) {
          parts.push({ type: 'text', content: textBefore });
        }
      }
      
      // Add photo marker (1-indexed in content, 0-indexed in array)
      const photoIndex = parseInt(match[1]) - 1;
      if (photoIndex >= 0 && photoIndex < photos.length) {
        parts.push({ type: 'photo', index: photoIndex, photo: photos[photoIndex] });
        usedPhotoIndices.add(photoIndex);
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text after last marker
    if (lastIndex < content.length) {
      const remainingText = content.substring(lastIndex).trim();
      if (remainingText) {
        parts.push({ type: 'text', content: remainingText });
      }
    }
    
    // If no markers found, just return the content as text
    if (parts.length === 0 && content) {
      parts.push({ type: 'text', content: content });
    }
    
    return { parts, usedPhotoIndices };
  };

  const { parts } = parseContent();

  return (
    <div className="space-y-6">
      {parts.map((part, idx) => {
        if (part.type === 'text') {
          // Render paragraphs
          return (
            <div key={idx} className="prose prose-lg max-w-none">
              {part.content.split('\n\n').map((paragraph, pIdx) => (
                <p key={pIdx} className="text-foreground leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          );
        } else if (part.type === 'photo' && part.photo) {
          // Render inline photo with caption-style presentation
          return (
            <figure 
              key={idx} 
              className="my-8 -mx-6 md:mx-0"
              data-testid={`inline-photo-${part.index}`}
            >
              <button
                onClick={() => onPhotoClick(part.index)}
                className="w-full block overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow cursor-zoom-in"
              >
                <img
                  src={getPhotoUrlWithWatermark(part.photo)}
                  alt={part.photo.filename || `Photo ${part.index + 1}`}
                  className="w-full h-auto max-h-[70vh] object-contain bg-gray-100"
                />
              </button>
              {part.photo.metadata?.description && (
                <figcaption className="mt-3 text-center text-sm text-muted-foreground italic px-4">
                  {part.photo.metadata.description}
                </figcaption>
              )}
            </figure>
          );
        }
        return null;
      })}
    </div>
  );
}

// Component to show remaining photos that weren't used inline
function RemainingPhotosGallery({ content, photos, onPhotoClick }) {
  // Find which photos were used inline
  const photoPattern = /\[PHOTO_(\d+)\]/g;
  const usedIndices = new Set();
  let match;
  
  while ((match = photoPattern.exec(content)) !== null) {
    usedIndices.add(parseInt(match[1]) - 1);
  }
  
  // Get unused photos
  const unusedPhotos = photos.filter((_, idx) => !usedIndices.has(idx));
  
  // If all photos were used inline or only 1-2 remain, don't show gallery
  if (unusedPhotos.length === 0) return null;
  
  // If content has no markers at all, show all photos in gallery (legacy articles)
  const hasMarkers = photoPattern.test(content);
  const photosToShow = hasMarkers ? unusedPhotos : photos;
  
  if (photosToShow.length === 0) return null;
  
  return (
    <section className="mt-12 md:mt-16" data-testid="photo-gallery">
      <h3 className="font-heading text-2xl font-semibold text-primary mb-6">
        {hasMarkers ? 'Autres photos' : 'Galerie Photos'}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photosToShow.map((photo, displayIdx) => {
          // Find original index for click handler
          const originalIdx = photos.findIndex(p => p.id === photo.id);
          return (
            <button
              key={photo.id}
              onClick={() => onPhotoClick(originalIdx)}
              className="relative aspect-square overflow-hidden rounded-lg group"
              data-testid={`gallery-photo-${displayIdx}`}
            >
              <img
                src={getPhotoUrlWithWatermark(photo)}
                alt={photo.filename}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors" />
            </button>
          );
        })}
      </div>
    </section>
  );
}

export default function ArticlePage({ preview = false }) {
  const { id } = useParams();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const canExportPdf = ["editor", "admin", "super_admin"].includes(user?.role);
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [commentForm, setCommentForm] = useState({
    author_name: "",
    author_email: "",
    content: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const handleExportPdf = async () => {
    if (!article) return;
    setExportingPdf(true);
    try {
      const key = article.slug || article.id;
      const res = await exportContentPdf("article", key);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeSlug = (article.slug || article.id || "export").replace(/[^a-z0-9-_]+/gi, "-");
      a.download = `itercatus-article-${safeSlug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF généré");
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Export PDF indisponible pour ce contenu");
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    loadArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, i18n.language]);

  // Phase E2 (2026-09-21) — <title> et <meta name="description"> pilotés par
  // la variante localisée quand elle existe (contenu déjà overlayé par le
  // backend via ?lng=). Fallback FR strict. Nettoyage automatique au démontage.
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const title = article.seo_title || article.title;
    if (title) document.title = `${title} — ITERCATUS`;
    const metaId = "article-i18n-meta-desc";
    let tag = document.querySelector(`meta#${metaId}`);
    if (article.meta_description) {
      if (!tag) {
        tag = document.createElement("meta");
        tag.id = metaId;
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", article.meta_description);
    }
    return () => {
      document.title = prevTitle;
      const t = document.querySelector(`meta#${metaId}`);
      if (t) t.remove();
    };
  }, [article]);

  const loadArticle = async () => {
    try {
      const [articleRes, commentsRes] = await Promise.all([
        getArticle(id, i18n.language),
        getArticleComments(id)
      ]);
      setArticle(articleRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error("Error loading article:", error);
      toast.error("Erreur lors du chargement de l'article");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createComment({
        article_id: id,
        ...commentForm
      });
      toast.success("Commentaire envoyé ! Il sera visible après modération.");
      setCommentForm({ author_name: "", author_email: "", content: "" });
    } catch (error) {
      toast.error("Erreur lors de l'envoi du commentaire");
    } finally {
      setSubmitting(false);
    }
  };

  const navigatePhoto = (direction) => {
    if (!article?.photos || selectedPhoto === null) return;
    const newIndex = selectedPhoto + direction;
    if (newIndex >= 0 && newIndex < article.photos.length) {
      setSelectedPhoto(newIndex);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-secondary">
        <Skeleton className="h-[60vh] w-full" />
        <div className="max-w-4xl mx-auto px-6 py-12 lg:max-w-5xl xl:max-w-6xl">
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-20 min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-bold text-primary mb-4">Article non trouvé</h1>
          <Link to="/">
            <Button className="btn-primary">Retour à l'accueil</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Hero picks the article's featured_image if returned in photos[], otherwise
  // falls back to the first available photo. `article.photos` is not
  // guaranteed to be ordered by `photo_ids`, so we must look it up explicitly
  // instead of assuming photos[0] is the cover.
  const mainPhoto =
    (article.featured_image &&
      article.photos?.find((p) => p.id === article.featured_image)) ||
    article.photos?.[0];
  const mainImageUrl = getPhotoUrlWithWatermark(mainPhoto) || "https://images.unsplash.com/photo-1724598674807-6acb225b98f2?w=1200";

  // Phase 14 STEP 4 — date de mise à jour significative (jour différent de la publication)
  const _pubDate = article.published_at || article.created_at;
  const _showUpdated =
    article.updated_at && _pubDate &&
    new Date(article.updated_at).toDateString() !== new Date(_pubDate).toDateString();
  const formattedUpdated = _showUpdated
    ? format(new Date(article.updated_at), "d MMMM yyyy", { locale: fr })
    : null;

  return (
    <div className={`min-h-screen bg-secondary ${preview ? "pb-16" : ""}`} data-testid="article-page">
      {preview && (
        <div className="fixed bottom-0 inset-x-0 z-[61] flex flex-wrap items-center justify-between gap-3 bg-amber-400 text-amber-950 px-4 md:px-8 py-3 text-xs md:text-sm font-bold tracking-widest shadow-[0_-4px_16px_rgba(0,0,0,0.12)]" data-testid="preview-banner">
          <span>PRÉVISUALISATION — NON PUBLIÉ</span>
          <Link to="/dashboard/intelligence" className="underline underline-offset-4 tracking-normal font-semibold" data-testid="preview-back-to-intelligence">← Retour à Intelligence</Link>
        </div>
      )}
      {/* Hero Image */}
      <section className="relative h-[60vh] md:h-[70vh]">
        <img
          src={mainImageUrl}
          alt={(mainPhoto?.source === "official_press" && mainPhoto?.metadata?.description) || article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
        {mainPhoto?.source === "official_press" && mainPhoto?.metadata?.description && (
          <p className="absolute bottom-3 right-4 md:right-12 text-[11px] text-white/80 italic" data-testid="hero-credit">{mainPhoto.metadata.description}</p>
        )}
        
        {/* Back Button */}
        <Link
          to="/"
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="h-5 w-5" />
          Retour
        </Link>

        {/* Export PDF (back-office only: editor / admin / super_admin) */}
        {canExportPdf && (
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            data-testid="article-export-pdf-btn"
            className="absolute top-24 right-6 md:right-12 flex items-center gap-2 rounded-full border border-white/25 bg-black/25 backdrop-blur px-4 py-2 text-xs md:text-sm font-medium tracking-wide text-white/90 hover:bg-black/40 hover:border-accent/70 hover:text-white transition disabled:opacity-60"
            aria-label="Exporter cet article en PDF"
          >
            <FileDown className="h-4 w-4" />
            {exportingPdf ? "Génération…" : "Exporter en PDF"}
          </button>
        )}

        {/* Article Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-20">
          <div className="max-w-4xl lg:max-w-5xl">
            {article.category_name && (
              <span className="inline-block px-3 py-1 bg-accent text-white text-xs font-medium tracking-wide uppercase mb-4">
                {article.category_name}
              </span>
            )}
            {article.kicker && (
              <p className="text-white/85 text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-3" data-testid="article-kicker">{article.kicker}</p>
            )}
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              {article.title}
            </h1>
            {article.subtitle && (
              <p className="text-white/90 text-base md:text-xl max-w-3xl mb-4 leading-snug" data-testid="article-subtitle">{article.subtitle}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm md:text-base">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {article.author_name || "Auteur"}
              </span>
              {article.status === "published" ? (
                <span className="flex items-center gap-2" data-testid="article-published-date">
                  <Calendar className="h-4 w-4" />
                  Publié le {format(new Date(_pubDate), "d MMMM yyyy", { locale: fr })}
                </span>
              ) : (
                <span className="flex items-center gap-2" data-testid="article-status-label">
                  <Calendar className="h-4 w-4" />
                  {preview ? "Prévisualisation · " : ""}{({ draft: "Brouillon", to_review: "Prêt à relire" })[article.status] || "Non publié"}
                </span>
              )}
              {article.destination && (
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {article.destination}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16 lg:max-w-5xl xl:max-w-6xl">
        {canExportPdf && article.status !== "published" && !(article.photos || []).length && (
          <div className="mb-8 border border-dashed border-amber-500 bg-amber-50 text-amber-900 px-5 py-3 rounded text-sm font-semibold tracking-wide" data-testid="article-visual-missing">
            VISUEL À COMPLÉTER — aucune image adaptée dans la médiathèque ITERCATUS (image d'en-tête provisoire, non publiée).
          </div>
        )}
        {article.excerpt && (
          <p className="font-accent text-xl md:text-2xl text-muted-foreground italic leading-relaxed mb-8 border-l-4 border-accent pl-6">
            {article.excerpt}
          </p>
        )}

        {/* Phase 14 STEP 3.3 — transparence partenaire (spécifique à l'article, data-driven) */}
        {article.show_disclosure && article.disclosure_text && (
          <div className="mb-8 border-l-4 border-sky-400 bg-sky-50 px-5 py-4 rounded-r" data-testid="article-partner-disclosure">
            <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 mb-1">
              Transparence{article.disclosure_partner ? ` — ${article.disclosure_partner}` : ""}
            </p>
            <p className="text-sm text-sky-900">{article.disclosure_text}</p>
          </div>
        )}

        {/* Phase 14 STEP 4 — note de correction / mise à jour (si renseignée) */}
        {article.correction_note && (
          <div className="mb-8 border-l-4 border-amber-400 bg-amber-50 px-5 py-4 rounded-r" data-testid="article-correction-note">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">
              Correction / mise à jour
              {article.correction_date ? ` — ${format(new Date(article.correction_date), "d MMMM yyyy", { locale: fr })}` : ""}
            </p>
            <p className="text-sm text-amber-900">{article.correction_note}</p>
          </div>
        )}
        {formattedUpdated && (
          <p className="text-sm text-muted-foreground mb-6" data-testid="article-updated-date">
            Mis à jour le {formattedUpdated}
          </p>
        )}
        
        {/* Word count indicator */}
        {article.word_count && (
          <p className="text-sm text-muted-foreground mb-8">
            Temps de lecture : ~{Math.ceil(article.word_count / 200)} min ({article.word_count} mots)
          </p>
        )}

        {/* Article content - NEW: use content_blocks if available */}
        <div className="article-content">
          {article.content_blocks && article.content_blocks.length > 0 ? (
            <ArticleContentBlocks 
              contentBlocks={article.content_blocks} 
              photos={article.photos || []} 
              onPhotoClick={setSelectedPhoto}
            />
          ) : (
            <ArticleContentWithPhotos 
              content={article.content} 
              photos={article.photos || []} 
              onPhotoClick={setSelectedPhoto}
            />
          )}
        </div>

        {/* Remaining photos gallery - only if NOT using content_blocks */}
        {!article.content_blocks && article.photos && article.photos.length > 0 && (
          <RemainingPhotosGallery 
            content={article.content}
            photos={article.photos}
            onPhotoClick={setSelectedPhoto}
          />
        )}

        {/* Signature auteur publique (Phase 13.c) — omission gracieuse si non configurée */}
        <AuthorSignature author={article.public_author} fallbackName={article.author_name} />

        {/* Comments Section */}
        {article.status === "published" && (
        <section className="mt-12 md:mt-16 border-t border-border pt-12" data-testid="comments-section">
          <h3 className="font-heading text-2xl font-semibold text-primary mb-8">
            Commentaires ({comments.length})
          </h3>

          {/* Comment List */}
          {comments.length > 0 ? (
            <div className="space-y-6 mb-12">
              {comments.map((comment) => (
                <div key={comment.id} className="bg-white p-6 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-primary">{comment.author_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(comment.created_at), "d MMMM yyyy", { locale: fr })}
                      </p>
                    </div>
                  </div>
                  <p className="text-muted-foreground">{comment.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground mb-8">Aucun commentaire pour le moment. Soyez le premier !</p>
          )}

          {/* Comment Form */}
          <div className="bg-white p-6 md:p-8 border border-border">
            <h4 className="font-heading text-xl font-semibold text-primary mb-6">Laisser un commentaire</h4>
            <form onSubmit={handleSubmitComment} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Nom</label>
                  <Input
                    value={commentForm.author_name}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, author_name: e.target.value }))}
                    placeholder="Votre nom"
                    required
                    className="input-underline"
                    data-testid="comment-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-2">Email</label>
                  <Input
                    type="email"
                    value={commentForm.author_email}
                    onChange={(e) => setCommentForm(prev => ({ ...prev, author_email: e.target.value }))}
                    placeholder="votre@email.com"
                    required
                    className="input-underline"
                    data-testid="comment-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Commentaire</label>
                <Textarea
                  value={commentForm.content}
                  onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Partagez votre avis..."
                  rows={4}
                  required
                  className="resize-none border-border focus:border-primary"
                  data-testid="comment-content-input"
                />
              </div>
              <Button 
                type="submit" 
                className="btn-primary" 
                disabled={submitting}
                data-testid="submit-comment-btn"
              >
                {submitting ? "Envoi..." : "Envoyer"}
              </Button>
              <p className="text-sm text-muted-foreground">
                Votre commentaire sera visible après validation par un modérateur.
              </p>
            </form>
          </div>
        </section>
        )}
      </article>

      {/* Photo Lightbox */}
      {selectedPhoto !== null && article.photos && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setSelectedPhoto(null)}
          data-testid="photo-lightbox"
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            data-testid="close-lightbox-btn"
          >
            <X className="h-8 w-8" />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); navigatePhoto(-1); }}
            className="absolute left-4 text-white/80 hover:text-white disabled:opacity-30"
            disabled={selectedPhoto === 0}
            data-testid="prev-photo-btn"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>

          <img
            src={getPhotoUrlWithWatermark(article.photos[selectedPhoto])}
            alt={article.photos[selectedPhoto].filename}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => { e.stopPropagation(); navigatePhoto(1); }}
            className="absolute right-4 text-white/80 hover:text-white disabled:opacity-30"
            disabled={selectedPhoto === article.photos.length - 1}
            data-testid="next-photo-btn"
          >
            <ChevronRight className="h-10 w-10" />
          </button>

          <div className="absolute bottom-4 text-white/60 text-sm">
            {selectedPhoto + 1} / {article.photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
